<template>
  <section-page
    title="Funnel"
    full-height
  >
    <template v-slot:right>
      <stats-funnel-filter
        @applyFilter="applyFilter"
        :isLoading="isLoading"
        :campaign-filter="campaignFilter"
        :is-using-campaign-filter="isUsingCampaignFilter"
        :funnel-timeframe="timeframe"
      ></stats-funnel-filter>
    </template>

    <app-loading-section
      :loading="isLoading"
    >
      <div class="funnel">
        <div class="columns">
          <div class="column is-3">
            <div
              class="key-container"
              v-for="(key, index) in keys"
              :key="index"
              :style="{ borderLeft: '4px solid ' + key.color }"
            >
              <div class="key-title has-text-weight-bold mb-50">
                <a v-if="key.campaignId" :href="'#campaignId=' + key.campaignId">
                  <icon-default
                    color="is-light"
                    :icon="$utils.getCampaignIcon(key.campaignId)"
                    size="is-small"
                  ></icon-default>
                  {{ key.label }}
                </a>
                <span v-else>{{ key.label }}</span>
                <input class="color-edit" type="color" v-model="key.color" @input="changeStepColor(index)">
              </div>
              <div v-if="isUsingCampaignFilter" class="key-data"><span class="has-text-weight-bold">Total Cost:</span> {{ key.totalCost | currency }}</div>
              <div v-if="isUsingCampaignFilter" class="key-data"><span class="has-text-weight-bold">Total Projected Profit:</span> {{ key.projectedProfit | currency }}</div>
              <div v-if="isUsingCampaignFilter" class="key-data"><span class="has-text-weight-bold">Total Actual Profit:</span> {{ key.actualProfit | currency }}</div>
              <div v-if="isUsingCampaignFilter" class="key-data"><span class="has-text-weight-bold">ROI:</span> {{ key.roi }}</div>
              <div v-if="isUsingCampaignFilter && key.campaignId !== null" class="key-data"><span class="has-text-weight-bold">{{ key.unitLabel }}:</span> {{ key.unitCount }}</div>
              <div class="key-data">
                <span class="has-text-weight-bold">Not a Lead:</span>
                <stats-funnel-records
                  v-if="key.notALeadCount > 0"
                  class="records-link"
                  api="records-not-a-lead"
                  :label="`${key.label} (Not a Lead)`"
                  :value="key.notALeadCount"
                  :campaignFilter="[key.campaignId]"
                  :timeframe="readableTimeframe"
                ></stats-funnel-records>
                <span v-else>0 Leads</span>
              </div>

              <div class="key-data">
                <span class="has-text-weight-bold">In Follow Up:</span>
                <stats-funnel-records
                  v-if="key.inFollowUpCount > 0"
                  class="records-link"
                  api="records-in-follow-up"
                  :label="`${key.label} (In Follow Up)`"
                  :value="key.inFollowUpCount"
                  :campaignFilter="[key.campaignId]"
                  :timeframe="readableTimeframe"
                ></stats-funnel-records>
                <span v-else>0 Leads</span>
              </div>

              <div class="key-data">
                <span class="has-text-weight-bold">Dead Deals:</span>
                <stats-funnel-records
                  v-if="key.deadDealCount > 0"
                  class="records-link"
                  api="records-dead"
                  :label="`${key.label} (Dead Deals)`"
                  :value="key.deadDealCount"
                  :campaignFilter="[key.campaignId]"
                  :timeframe="readableTimeframe"
                ></stats-funnel-records>
                <span v-else>0 Leads</span>
              </div>
            </div>
          </div>

          <div class="column is-9">
            <div v-if="this.computedTimeframe.type === 'today'" class="has-text-centered has-text-weight-bold mb-200">{{ readableTimeframe.start_at.format('MMM DD h:mm a') }} - {{ readableTimeframe.end_at.format('MMM DD h:mm a') }}</div>
            <div v-else class="has-text-centered has-text-weight-bold mb-200">{{ readableTimeframe.start_at.format('MMM DD YYYY') }} - {{ readableTimeframe.end_at.format('MMM DD YYYY') }}</div>

            <div
              v-for="(step, index) in steps"
              @mouseenter="showDetails(index)"
              @mouseleave="hideDetails($event, index)"
              class="funnel-step"
              :key="index"
            >
              <div class="step-data">
                <stats-funnel-step-dropdown
                  v-if="isMultipleCampaigns && step.value > 0"
                  class="mb-50 is-size-6 records-link"
                  :step="step"
                  :campaignFilter="campaignFilter"
                  :timeframe="readableTimeframe"
                ></stats-funnel-step-dropdown>

                <stats-funnel-records
                  v-else-if="step.value > 0"
                  class="mb-50 is-size-6 records-link"
                  :api="step.api"
                  :label="step.label"
                  :value="step.value"
                  :campaignFilter="isUsingCampaignFilter ? campaignFilter : null"
                  :timeframe="readableTimeframe"
                  is-bold
                ></stats-funnel-records>
                <div v-else class="mb-50 is-size-6 has-text-weight-bold">0 Leads</div>

                <div class="is-size-7 mb-25 has-text-primary has-text-weight-bold">
                  {{ step.label }}

                  <app-info-tip
                    :tip="step.description"
                    position="is-right"
                  ></app-info-tip>
                </div>

                <div v-if="steps[0].value && index > 0" class="is-size-7 mb-25 has-text-weight-bold">{{ getPercent(step.value, steps[0].value) }}</div>
                <div v-if="step.value > 0 && isUsingCampaignFilter" class="is-size-7">${{ (step.value > 0 ? totalCostOfSelectedCampaigns / step.value : 0).toFixed(2) }} cost per {{ step.costPerLabel }}</div>

              </div>

              <div @click="toggleStick($event, index)" class="step-block-container">
                <div class="step-block" :style="{ width: getPercent(step.value, steps[0].value) }">
                  <div
                    v-for="(campaign, index) in step.campaigns"
                    class="campaign-block"
                    :style="{ width: getPercent(campaign.value, step.value, true), background: campaign.color }"
                    :key="index"
                  ></div>
                </div>

                <transition name="fade" mode="out-in">
                  <div class="detail-btns" v-show="step.isShowingDetails && step.isOverflowed && isMultipleCampaigns">
                    <button class="left-btn" @click="scrollDetailLeft($event, index)">
                      <icon-default icon="left"></icon-default>
                    </button>
                    <button class="right-btn" @click="scrollDetailRight($event, index)">
                      <icon-default icon="right"></icon-default>
                    </button>
                  </div>
                </transition>

                <transition name="fade" mode="out-in">
                  <stats-funnel-step-detail
                    :class="{'curve-border': !step.isOverflowed}"
                    ref="stepDetail"
                    v-show="step.isShowingDetails && isMultipleCampaigns"
                    :step="step"
                  ></stats-funnel-step-detail>
                </transition>

                <div v-show="step.isSticky && isMultipleCampaigns" class="sticky-label">Sticky</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </app-loading-section>
  </section-page>
</template>

<script>
import StatsFunnelRecords from './components/StatsFunnelRecords';
import StatsFunnelStepDropdown from './components/StatsFunnelStepDropdown';
import StatsFunnelStepDetail from './components/StatsFunnelStepDetail';
import StatsFunnelFilter from "./components/StatsFunnelFilter";
import moment from "moment";
import AppInfoTip from "../../components/app/AppInfoTip.vue";

export default {
  components: {
    AppInfoTip,
    StatsFunnelFilter,
    StatsFunnelRecords,
    StatsFunnelStepDropdown,
    StatsFunnelStepDetail
  },
  data() {
    return {
      initialLoaded: false,
      isLoading: true,
      timeframe: 3,
      readableTimeframe: { start: '', end: '', start_at: null, end_at: moment() },
      isUsingCampaignFilter: true,
      completionCount: 0,
      campaignFilter: [],
      campaigns: null,
      steps: [],
      keys: [],
      funnelCustomDates: [],
      colors: ['#9AADBF','#75798a', '#b49d9d', '#c1c5a6', '#AC9FBB', '#99EDCC', '#6b996e', '#8c5667']
    }
  },
  computed: {
    activeCampaigns() {
      return this.$store.getters.activeCampaigns;
    },
    computedTimeframe() {
      const timeframes = [
        { id: 1, type: 'today' },
        { id: 2, type: 'day', value: 7 },
        { id: 3, type: 'day', value: 14 },
        { id: 4, type: 'month', value: 1 },
        { id: 5, type: 'month', value: 3 },
        { id: 6, type: 'month', value: 6 },
        { id: 7, type: 'year', value: 1 },
        { id: 8, type: 'custom' },
      ];

      return timeframes.find(time => time.id === this.timeframe);
    },
    totalCostOfSelectedCampaigns() {
      const selectedCampaignCosts = this.campaigns.filter(c => this.campaignFilter.includes(c.id)).map(c => parseFloat(c.total_costs));
      return selectedCampaignCosts.reduce((a,b) => a + b, 0);
    },
    isMultipleCampaigns() {
      // users are reporting a bug that the step details aren't showing up
      // it appears that Micheal is hiding the step details if only one campaign
      // I'm not sure if we'll want to revert so I'm just doing this
      return true;
      // return (this.isUsingCampaignFilter && this.campaignFilter.length > 1);
    },
    funnelFilters() {
      return this.$store.getters["stats/funnelFilters"];
    },
  },
  created() {
    this.init();
    this.initialLoaded = true;
  },
  methods: {
    init() {
      this.campaignFilter = (this.$route.query.campaignId) ? [this.$route.query.campaignId] : this.funnelFilters.campaigns;
      this.timeframe = this.funnelFilters.timeframe;
      this.funnelCustomDates = this.funnelFilters.customDates;

      this.getFunnel();
    },
    getFunnel() {
      let campaignIds = this.isUsingCampaignFilter ? this.campaignFilter : [this.campaigns.map(c => c.id), null];

      this.completionCount = 0;

      this.steps = [
        {
          label: 'New Leads',
          description: 'All leads created in timeframe designated',
          value: 0,
          api: '',
          campaigns: []
        },
        {
          label: 'Qualified Leads',
          description: 'All leads from the New Leads bucket that are currently in a status other than a Non Lead status',
          value: 0,
          api: '',
          campaigns: []
        },
        {
          label: 'Appointments Set',
          description: 'All leads from the New Leads bucket that have had an appointment',
          value: 0,
          api: '',
          campaigns: []
        },
        {
          label: 'Offer Made',
          description: 'All leads from the New Leads bucket that have at least one buying offer',
          value: 0,
          api: '',
          campaigns: []
        },
        {
          label: 'Offer Accepted',
          description: 'All leads from the New Leads bucket that have an Accepted Date filled in on buying info section',
          value: 0,
          api: '',
          campaigns: []
        },
        {
          label: 'Contract',
          description: 'All leads from the New Leads bucket that have an Contract Date filled in on buying info section',
          value: 0,
          api: '',
          campaigns: []
        },
        {
          label: 'Purchased',
          description: 'All leads from the New Leads bucket that have an Closed Date filled in on buying info section',
          value: 0,
          api: '',
          campaigns: []
        },
        {
          label: 'Completed',
          description: 'All leads from the New Leads bucket that are currently in a Completed status',
          value: 0,
          api: '',
          campaigns: []
        },
      ];

      this.keys = [];

      if (this.computedTimeframe.type === 'today') {
        this.readableTimeframe.start_at = moment().startOf('day');
      } else if (this.computedTimeframe.type === 'custom') {
        this.readableTimeframe.start_at = moment(this.funnelCustomDates[0]).startOf('day');
        this.readableTimeframe.end_at = moment(this.funnelCustomDates[1]).endOf('day');
      } else {
        this.readableTimeframe.start_at = moment().subtract(this.computedTimeframe.value, this.computedTimeframe.type);
      }

      const campaignStatsData = {
        start_at: this.readableTimeframe.start_at.toISOString(),
        end_at: this.readableTimeframe.end_at.toISOString(),
        campaigns: (this.campaignFilter.length !== 0) ? this.campaignFilter : this.getAllCampaigns()
      };

      this.$api.stats.getFunnelCampaignStats(campaignStatsData).then(res => {
        this.campaigns = res.data

        campaignIds = (campaignIds.length !== 0) ? campaignIds : this.getAllCampaigns();

        for (let i = 0; i < campaignIds.length; i++) {
          let color = (i <= this.colors.length) ? this.colors[i] : this.getRandomColor();
          this.getData(this.readableTimeframe.start_at.toISOString(), this.readableTimeframe.end_at.toISOString(), campaignIds[i], color, this.campaigns.length);
        }
      });
    },
    getData(start_at, end_at, campaignId, color, totalCount) {
      const map = {
        dealsPurchasedCount: { label: 'Purchased', api: 'deals-purchased', costPerLabel: 'purchase' },
        recordsCreatedCount: { label: 'New Leads', api: 'records-created', costPerLabel: 'new record' },
        recordsDeadCount: { label: 'Dead Deals', api: 'records-dead' },
        recordsInFollowUpCount: { label: 'In Follow Up', api: 'records-in-follow-up' },
        recordsNotALeadCount: { label: 'Not a Lead', api: 'records-not-a-lead' },
        recordsQualifiedLeadsCount: { label: 'Qualified Leads', api: 'records-qualified-leads', costPerLabel: 'lead' },
        recordsCompletedLeadsCount: { label: 'Completed', api: 'records-completed-leads', costPerLabel: 'completed' },
        recordsWithAcceptedOffersCount: { label: 'Offer Accepted', api: 'records-with-accepted-offers', costPerLabel: 'accepted offer' },
        recordsWithAppointmentsSetCount: { label: 'Appointments Set', api: 'records-with-appointments-set', costPerLabel: 'appointment' },
        recordsWithContractsCount: { label: 'Contract', api: 'records-with-contracts', costPerLabel: 'contract' },
        recordsWithOffersMadeCount: { label: 'Offer Made', api: 'records-with-offers-made', costPerLabel: 'offer made' },
      };

      const campaign = this.campaigns.find(c => c.id === campaignId);
      const campaigns = this.isUsingCampaignFilter ? [campaignId] : null;

      this.$api.stats.getAllFunnelCount({ start_at, end_at, campaigns }).then(res => {
        const key = {
          campaignId: campaign.id,
          label: campaign.label,
          channel: campaign.channel,
          actualProfit: campaign.actual_profit,
          projectedProfit: campaign.projected_profit,
          roi: campaign.total_costs ? Math.round(((campaign.actual_profit - campaign.total_costs) / campaign.total_costs) * 100) + '%' : 'infinite',
          notALeadCount: res.data.recordsNotALeadCount,
          inFollowUpCount: res.data.recordsInFollowUpCount,
          deadDealCount: res.data.recordsDeadCount,
          totalCost: campaign.total_costs,
          unitCount: campaign.total_unit_counts,
          unitLabel: campaign.unit_label,
          color
        }

        this.keys.push(key);

        for (const [key, value] of Object.entries(res.data)) {
          for (const step of this.steps) {
            if (map[key].label === step.label) {
              const campaignData = {
                id: campaign.id,
                label: campaign.label,
                value,
                color,
                costPer: (value > 0 ? campaign.total_costs / value : 0).toFixed(2),
              };

              step.value = step.value + value;
              step.api = map[key].api;
              step.costPerLabel = map[key].costPerLabel;
              step.isShowingDetails = false;
              step.isSticky = false;
              step.isOverflowed = false;

              if (campaignData.value !== 0) {
                step.campaigns.push(campaignData);
              }
            }
          }
        }

        if (this.isUsingCampaignFilter) {
          this.completionCount++;
          if (this.completionCount === totalCount) {
            this.isLoading = false;
          }
        } else {
          this.isLoading = false;
        }
      });
    },
    showDetails(index) {
      const container = this.$refs.stepDetail[index].$refs.outer;
      const wrapper = this.$refs.stepDetail[index].$refs.inner;

      this.steps[index].isShowingDetails = true;

      setTimeout(() => {
        if (wrapper.offsetWidth > container.offsetWidth) {
          this.steps[index].isOverflowed = true;
        }

        this.$forceUpdate();
      }, 50);

      this.$forceUpdate();
    },
    hideDetails(event, index) {
      if (!this.steps[index].isSticky) {
        event.stopPropagation();
        this.steps[index].isShowingDetails = false;
        this.$forceUpdate();
      }
    },
    scrollDetailRight(event, index) {
      event.stopPropagation();
      const container = this.$refs.stepDetail[index].$refs.outer;
      container.scrollLeft += 100;
    },
    scrollDetailLeft(event, index) {
      event.stopPropagation();
      const container = this.$refs.stepDetail[index].$refs.outer;
      container.scrollLeft -= 100;
    },
    toggleStick(event, index) {
      if (this.steps[index].isSticky) {
        event.stopPropagation();
        this.steps[index].isSticky = false;
      } else {
        this.steps[index].isSticky = true;
      }

      this.$forceUpdate();
    },
    getRandomColor() {
      const letters = '0123456789ABCDEF';
      let color = '#';

      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }

      return color;
    },
    getPercent(val, total, isFloor) {
      const percent = (val / (total / 100));

      if (total === 0) {
        return '0%';
      }

      if (isFloor) {
        return (Math.floor((percent + Number.EPSILON) * 100) / 100) + '%';
      } else {
        return (Math.round((percent + Number.EPSILON) * 100) / 100) + '%';
      }
    },
    getAllCampaigns() {
      const campaigns = this.$utils.clone(this.activeCampaigns);
      const ids = campaigns.map(c => c.id);
      ids.push(null);
      return ids
    },
    applyFilter(filter) {
      this.campaignFilter = filter.campaigns;
      this.isUsingCampaignFilter = filter.isUsingCampaignFilter;
      this.timeframe = filter.funnelTimeframe;
      this.funnelCustomDates = filter.funnelCustomDates;
      this.isLoading = true;

      this.$store.commit("stats/setOneFilter", ['funnel', 'campaigns', filter.campaigns]);
      this.$store.commit('stats/setOneFilter', ['funnel', 'timeframe', filter.funnelTimeframe]);
      this.$store.commit('stats/setOneFilter', ['funnel', 'customDates', filter.funnelCustomDates]);

      this.getFunnel();
    },
    changeStepColor(index) {
      for (const step of this.steps) {
        step.campaigns[index].color = this.keys[index].color;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.funnel {
  position: relative;
  min-height: 100px;
  margin-top: 1rem;
}

.funnel-step {
  border-bottom: 1px solid #777;
  height: 125px;
  position: relative;

  &:last-of-type {
    border-bottom: none;
  }
}

.step-data {
  display: inline-block;
  position: absolute;
  top: 10px;
  width: 150px;
  padding-right: 4px;
}

.step-block-container {
  position: relative;
  height: 100%;
  margin-left: 150px;
}

.step-block {
  display: inline-block;
  height: 100%;
  width: 100%;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
  border-radius: 5px;
  overflow: hidden;
  @include clearfix();
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}

.campaign-block {
  height: 100%;
  float: left;
}

.key-container {
  padding: 4px;
  padding-left: 10px;
  margin-bottom: 14px;
}

.key-data {
  font-size: 11px;
  margin-bottom: 2px;
}

.records-link {
  text-decoration: underline;
  &:hover {
    color: $primary;
  }
}

.curve-border {
  border-radius: 5px;
  width: calc(100% - 30px);
  overflow: hidden;
}

.detail-btns {
  position: absolute;
  width: 100%;
  top: 50%;
  transform: translate(0, -50%);
  @include clearfix();
  height: calc(100% - 20px);
  overflow: hidden;
  padding: 0 11px;

  .left-btn {
    float: left;
    height: 100%;
    border-radius: 5px 0 0 5px;
    border: none;
    background: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: 0.3s;
    padding: 0;
    outline: none;
    &:hover {
      background: #fff;
    }
  }

  .right-btn {
    float: right;
    height: 100%;
    border-radius: 0 5px 5px 0;
    border: none;
    background: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: 0.3s;
    padding: 0;
    outline: none;
    &:hover {
      background: #fff;
    }
  }
}

.sticky-label {
  text-align: center;
  font-weight: bold;
  color: $primary;
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translate(-50%, 0);
  background: #fff;
  padding: 2px 8px;
  font-size: 11px;
  border-radius: 5px;
}

.key-title {
  a {
    color: $text;

    &:hover {
      color: $primary;
    }
  }
}

.color-edit {
  cursor: pointer;
}

input[type="color"] {
	-webkit-appearance: none;
	border: none;
	width: 22px;
	height: 12px;
}

input[type="color"]::-webkit-color-swatch-wrapper {
	padding: 0;
}

input[type="color"]::-webkit-color-swatch {
	border: none;
}
</style>
